import LegalDocument from "../components/LegalDocument";
import usePageMeta from "../hooks/usePageMeta";

const UPDATED = "28 June 2026";

const INTRO = (
    <>
        <p>
            These Terms & Conditions govern your access to and use of this
            website (
            <a
                href="https://strataroofmanagement.com.au"
                className="text-srm-navy font-semibold underline-offset-4 hover:underline"
            >
                strataroofmanagement.com.au
            </a>
            ) and the publicly available content, articles, guides and
            enquiry forms published on it.
        </p>
        <p>
            Strata Roof Management Pty Ltd (trading as <strong>SRM</strong>)
            is an Australian independent consultancy providing roof condition
            assessment, storm and insurance investigation, compliance
            reporting, lifecycle and capital works planning, owner-side
            project oversight and long-term roof asset management services
            for strata communities, body corporates, building managers and
            asset owners across Australia. SRM never quotes or executes
            repair works and never accepts commission on works that follow a
            report — every engagement is structurally independent.
        </p>
        <p>
            By using this website you accept these Terms in full. If you do
            not accept any part of these Terms, please discontinue use of the
            site.
        </p>
    </>
);

const SECTIONS = [
    {
        id: "general-information",
        heading: "General information only",
        body: (
            <>
                <p>
                    All content on this website — including articles, guides,
                    explainers, case studies and Resources Knowledge Centre
                    material — is published for general informational
                    purposes only. It is not a substitute for a site-specific
                    inspection, professional advice or a written engagement
                    deliverable.
                </p>
                <p>
                    No roof can be reliably assessed from a website alone.
                    Building condition, compliance, insurance exposure and
                    capital-works planning are influenced by factors that
                    require physical inspection, documented evidence and
                    written professional judgement. Do not rely on website
                    content as the sole basis for any operational, financial
                    or legal decision regarding a roof asset.
                </p>
                <p>
                    For decision-grade advice, please engage SRM directly so
                    a defensible, site-specific report can be produced for
                    your building.
                </p>
            </>
        ),
    },
    {
        id: "inspection-reports",
        heading: "Inspection reports — scope, assumptions and limitations",
        body: (
            <>
                <p>
                    Inspection reports, condition assessments, compliance
                    reports, insurance investigations, lifecycle plans and
                    any other deliverables produced by SRM are governed by
                    their own written scope, assumptions and limitations
                    section. Those terms form part of the report itself and
                    take precedence over anything stated on this website in
                    relation to that engagement.
                </p>
                <p>
                    Common limitations that apply to most SRM deliverables
                    include (but are not limited to):
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        Observations are limited to areas that were
                        physically accessible or visible (including via drone
                        imagery where stated) on the date of inspection.
                    </li>
                    <li>
                        Hidden defects, concealed structural conditions or
                        latent issues not reasonably observable on the
                        inspection date are excluded.
                    </li>
                    <li>
                        Service-life estimates are professional judgements
                        based on observed condition and known industry
                        benchmarks — they are not guarantees of remaining
                        life and may change with weather events, occupancy
                        changes or maintenance activity.
                    </li>
                    <li>
                        Reports are issued on the assumption that information
                        and access provided by the client (or its agents) is
                        complete and accurate.
                    </li>
                    <li>
                        Each report is provided to the named addressee for
                        the stated purpose only and is not transferable to a
                        third party without SRM&apos;s written consent.
                    </li>
                </ul>
                <p>
                    Where any part of these website Terms conflicts with the
                    specific scope, assumptions and limitations of an issued
                    report, the report governs in relation to that
                    engagement.
                </p>
            </>
        ),
    },
    {
        id: "independence",
        heading: "Independence — what SRM does and does not do",
        body: (
            <>
                <p>
                    SRM operates as an independent consultancy. To preserve
                    that independence:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        SRM does <strong>not</strong> quote, perform or
                        sub-contract roof repairs or roof installation works.
                    </li>
                    <li>
                        SRM does <strong>not</strong> receive commissions,
                        rebates, kick-backs or financial incentives from
                        roofing contractors, materials suppliers or any
                        repair-linked party.
                    </li>
                    <li>
                        SRM does <strong>not</strong> sell, recommend or
                        endorse specific contractor brands as a function of a
                        commercial relationship.
                    </li>
                </ul>
                <p>
                    Where a SRM report references a contractor or product,
                    the reference is made solely on technical merit, evidence
                    and the client&apos;s interest — never on a commercial
                    relationship.
                </p>
            </>
        ),
    },
    {
        id: "no-warranty",
        heading: "No warranty on website content",
        body: (
            <>
                <p>
                    Website content is provided &quot;as is&quot; and &quot;as available&quot;.
                    While we take reasonable care to ensure accuracy at the
                    time of publication, SRM makes no warranties or
                    representations as to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>
                        The completeness, currency or accuracy of any
                        article, guide or statement.
                    </li>
                    <li>
                        The website being uninterrupted, error-free, or free
                        of viruses or other harmful components.
                    </li>
                    <li>
                        Any specific outcome resulting from reliance on
                        website content.
                    </li>
                </ul>
                <p>
                    To the maximum extent permitted by Australian Consumer
                    Law, SRM excludes liability for any indirect,
                    consequential or economic loss arising from your use of
                    the website. Nothing in these Terms excludes any
                    non-excludable statutory guarantee.
                </p>
            </>
        ),
    },
    {
        id: "intellectual-property",
        heading: "Intellectual property",
        body: (
            <>
                <p>
                    All content on this website — including written
                    material, the SRM brand, logos, page layouts, photographs
                    and the structure and presentation of articles — is
                    owned by, or licensed to, Strata Roof Management Pty Ltd
                    and is protected by Australian and international
                    copyright laws.
                </p>
                <p>
                    You may view and print individual pages for personal,
                    non-commercial review. You may not reproduce,
                    redistribute, republish, modify or commercially exploit
                    any content without SRM&apos;s prior written consent.
                </p>
                <p>
                    Quoting short extracts with a clear and visible
                    attribution back to{" "}
                    <a
                        href="https://strataroofmanagement.com.au"
                        className="text-srm-navy font-semibold underline-offset-4 hover:underline"
                    >
                        strataroofmanagement.com.au
                    </a>{" "}
                    is permitted under standard fair-use principles.
                </p>
            </>
        ),
    },
    {
        id: "external-links",
        heading: "External links",
        body: (
            <>
                <p>
                    The website may contain links to third-party websites for
                    convenience. SRM has no control over the content,
                    accuracy or privacy practices of linked third-party sites
                    and accepts no responsibility for them. The inclusion of
                    a link is not an endorsement.
                </p>
            </>
        ),
    },
    {
        id: "engagement-terms",
        heading: "Engagement terms",
        body: (
            <>
                <p>
                    Engagement of SRM for assessments, reporting, project
                    oversight or roof asset management is governed by a
                    separate written engagement document agreed between SRM
                    and the client. Nothing on this website constitutes a
                    binding engagement or a fee proposal.
                </p>
                <p>
                    Submission of an enquiry through this website
                    acknowledges that SRM may use the information you provide
                    to respond to your enquiry and prepare a tailored fee
                    proposal. Submitting an enquiry does not create an
                    engagement.
                </p>
            </>
        ),
    },
    {
        id: "jurisdiction",
        heading: "Governing law and jurisdiction",
        body: (
            <>
                <p>
                    These Terms are governed by the laws of the State of
                    Queensland and the Commonwealth of Australia. You submit
                    to the non-exclusive jurisdiction of the courts of
                    Queensland and the courts competent to hear appeals from
                    them.
                </p>
            </>
        ),
    },
    {
        id: "changes",
        heading: "Changes to these Terms",
        body: (
            <>
                <p>
                    SRM may amend these Terms from time to time. The current
                    version is always published on this page. Continued use
                    of the website after an amendment constitutes acceptance
                    of the revised Terms.
                </p>
            </>
        ),
    },
    {
        id: "contact",
        heading: "Contact",
        body: (
            <>
                <p>
                    For any question about these Terms — or any aspect of an
                    SRM engagement — please contact us:
                </p>
                <div className="mt-4 rounded-md border border-srm-line bg-white p-6 space-y-2">
                    <p className="font-display font-bold text-srm-navy">
                        Strata Roof Management Pty Ltd
                    </p>
                    <p>
                        Email:{" "}
                        <a
                            href="mailto:info@strataroofmanagement.com.au"
                            className="text-srm-navy font-semibold underline-offset-4 hover:underline"
                        >
                            info@strataroofmanagement.com.au
                        </a>
                    </p>
                    <p>
                        Amanda Belot — Business Development:{" "}
                        <a
                            href="tel:+61407053267"
                            className="text-srm-navy font-semibold underline-offset-4 hover:underline"
                        >
                            0407 053 267
                        </a>
                    </p>
                    <p>
                        Andy Nagy — General Manager:{" "}
                        <a
                            href="tel:+61497372914"
                            className="text-srm-navy font-semibold underline-offset-4 hover:underline"
                        >
                            0497 372 914
                        </a>
                    </p>
                    <p className="text-[14px] text-srm-body/85">
                        Sunshine Coast, Queensland, Australia · Service area:
                        Australia-wide
                    </p>
                </div>
            </>
        ),
    },
];

export default function Terms() {
    usePageMeta({
        title: "Terms & Conditions — SRM",
        description:
            "Terms of use for the Strata Roof Management website and the scope, assumptions and limitations that apply to SRM assessment reports.",
        path: "/terms",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Terms & Conditions", url: "/terms" },
        ],
    });

    return (
        <LegalDocument
            eyebrow="Legal"
            title="Terms & Conditions"
            updated={UPDATED}
            intro={INTRO}
            sections={SECTIONS}
            testIdPrefix="terms"
        />
    );
}
